import * as React from 'react'
import { View, ViewStyle } from 'react-native'
import { spacing, SpacingSize } from './types'
import { useBreakpoints } from './hooks/useBreakpoints'

type Columns = 1 | 2 | 3 | 4 | 5
type ResponsiveColumns = {
	mobile?: Columns
	tablet?: Columns
	desktop?: Columns
}

type ResponsiveSpace = {
	mobile?: SpacingSize
	tablet?: SpacingSize
	desktop?: SpacingSize
}

interface TilesProps {
	children: React.ReactNode
	columns?: Columns | ResponsiveColumns
	space?: SpacingSize | ResponsiveSpace
	style?: ViewStyle
}

export function Tiles({ children, columns = 1, space = 'md', style }: TilesProps) {
	const { isMobile, isTablet, isDesktop } = useBreakpoints()
	const arrayChildren = React.Children.toArray(children)

	// Determine the number of columns based on responsive props
	let activeColumns: number
	if (typeof columns === 'object') {
		if (isMobile && columns.mobile) {
			activeColumns = columns.mobile
		} else if (isTablet && columns.tablet) {
			activeColumns = columns.tablet
		} else if (isDesktop && columns.desktop) {
			activeColumns = columns.desktop
		} else {
			activeColumns = 1
		}
	} else {
		activeColumns = columns
	}

	// Determine the spacing based on responsive props
	let activeSpace: number
	if (typeof space === 'object') {
		if (isMobile && space.mobile) {
			activeSpace = spacing[space.mobile]
		} else if (isTablet && space.tablet) {
			activeSpace = spacing[space.tablet]
		} else if (isDesktop && space.desktop) {
			activeSpace = spacing[space.desktop]
		} else {
			activeSpace = spacing.md
		}
	} else {
		activeSpace = spacing[space]
	}

	const containerStyle: ViewStyle = {
		width: '100%',
		...style,
	}

	const rowStyle: ViewStyle = {
		flexDirection: 'row',
		flexWrap: 'wrap',
		width: '100%',
		marginHorizontal: -activeSpace / 2,
	}

	const getColumnStyle = (width: number = 1): ViewStyle => ({
		width: `${(100 * width) / activeColumns}%`,
		padding: activeSpace / 2,
	})

	// Group children into rows, respecting tile widths
	const rows: React.ReactNode[][] = []
	let currentRow: React.ReactNode[] = []
	let currentRowWidth = 0

	arrayChildren.forEach((child, index) => {
		if (React.isValidElement(child)) {
			const tileWidth = (child.props as any)?.width || 1
			const activeTileWidth =
				typeof tileWidth === 'object'
					? (isMobile && tileWidth.mobile) ||
						(isTablet && tileWidth.tablet) ||
						(isDesktop && tileWidth.desktop) ||
						1
					: tileWidth

			// If this tile would overflow the row, start a new row
			if (currentRowWidth + activeTileWidth > activeColumns) {
				rows.push(currentRow)
				currentRow = []
				currentRowWidth = 0
			}

			currentRow.push(child)
			currentRowWidth += activeTileWidth
		}

		// If this is the last child, add the current row
		if (index === arrayChildren.length - 1) {
			rows.push(currentRow)
		}
	})

	return (
		<View style={containerStyle}>
			<View style={rowStyle}>
				{rows.map((row, rowIndex) => (
					<React.Fragment key={rowIndex}>
						{row.map((child, colIndex) => {
							if (React.isValidElement(child)) {
								const tileWidth = (child.props as any)?.width || 1
								const activeTileWidth =
									typeof tileWidth === 'object'
										? (isMobile && tileWidth.mobile) ||
											(isTablet && tileWidth.tablet) ||
											(isDesktop && tileWidth.desktop) ||
											1
										: tileWidth
								return (
									<View
										key={colIndex}
										style={[
											getColumnStyle(activeTileWidth),
											// Remove right padding for last item in each row
											colIndex === row.length - 1 && { paddingRight: 0 },
										]}
									>
										{child}
									</View>
								)
							}
							return null
						})}
					</React.Fragment>
				))}
			</View>
		</View>
	)
}

// Example usage:
/*
<Tiles
  columns={{ mobile: 2, tablet: 3, desktop: 4 }}
  space={{ mobile: 'small', tablet: 'medium', desktop: 'large' }}
>
  <Tile width={{ mobile: 1, tablet: 2, desktop: 3 }}>Wide Tile</Tile>
  <Tile>Normal Tile</Tile>
  <Tile>Normal Tile</Tile>
  <Tile width={2}>Fixed Width Tile</Tile>
  <Tile>Normal Tile</Tile>
</Tiles>
*/
